/**
 * RC Hobby Shop — live product results under the catalog search bar.
 *
 * Progressive enhancement over the plain <form>: without JavaScript the form
 * still submits to the search page. With it, typing shows matching products
 * (thumbnail, title, part number, price) from Shopify's predictive search
 * endpoint (/search/suggest.json) — the same index the search page uses.
 * v21 (2026-09-22): shopper shorthand (xmaxx, trx4, bl2s, a-arms…) is rewritten to the
 * catalog's spelling before searching — see REWRITES.
 */
const MIN_CHARS = 2;
const DEBOUNCE_MS = 150;
const LIMIT = 8;
const FIELDS = 'title,product_type,variants.title,variants.sku,vendor,tag';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/*
 * Shopper shorthand -> the spelling Traxxas uses in product titles.
 * Shopify search splits words at hyphens and spaces ("X-Maxx" = "x maxx") but never
 * joins them, so "xmaxx" or "trx4" found nothing. Search & Discovery no longer offers
 * synonym groups (Sept 2026), so the search box rewrites the query before searching.
 * Each rule was checked against the catalog titles (2026-09-22). Terms that already
 * work (e.g. "diff" as a prefix of "differential", "udr") are deliberately left out.
 */
const REWRITES = [
  [/\b([xet])\s?maxx\b/g, '$1-maxx'],            // xmaxx, emaxx, tmaxx
  [/\be\s?revo\b/g, 'e-revo'],                    // erevo
  [/\btrx\s?(\d)(mt|m)?\b/g, 'trx-$1$2'],         // trx4, trx4m, trx4mt, trx6
  [/\bbl\s?(\d)s\b/g, 'bl-$1s'],                  // bl2s
  [/\bvxl\s?(\d)s\b/g, 'vxl-$1s'],                // vxl4s, vxl6s, vxl8s
  [/\bxl\s?(\d)\b/g, 'xl-$1'],                    // xl5
  [/\b4\s?tec\b/g, '4-tec'],                      // 4tec
  [/\bxo\s?1\b/g, 'xo-1'],                        // xo1
  [/\bez\s?(peak|start)\b/g, 'ez-$1'],            // ezpeak, ezstart
  [/\bmini(maxx|xrt|slash|rally)\b/g, 'mini $1'], // minimaxx, minixrt
  [/\b(drag|nitro)slash\b/g, '$1 slash'],         // dragslash
  [/\b(slash|rustler|stampede|bandit|bigfoot|jato|sledge|maxx)(4x4|2wd)\b/g, '$1 $2'],
  [/\bwide\s(maxx)\b/g, 'widemaxx'],
  [/\bla\strax\b/g, 'latrax'],
  [/\bbig\sfoot\b/g, 'bigfoot'],
  [/\bf\s?150\b/g, 'f-150'],
  [/\bk5\b/g, 'blazer'],
  [/\bshells?\b/g, 'body'],
  [/\brims?\b/g, 'wheels'],
  [/\btyres?\b/g, 'tires'],
  [/\btrann(?:y|ies)\b/g, 'transmission'],
  [/\bknuckles?\b/g, 'steering blocks'],
  [/\bc[\s-]?hubs?\b/g, 'caster blocks'],
  [/\ba[\s-]?arms?\b/g, 'suspension arms'],
  [/\bdrive\sshafts?\b/g, 'driveshaft'],
  [/\bcvds?\b/g, 'cv driveshaft'],
  [/\bhub\scarriers?\b/g, 'stub axle carriers'],
];

/** The query Shopify should see; returns the input unchanged when no rule applies. */
function normalizeQuery(term) {
  const lower = term.toLowerCase().replace(/\s+/g, ' ').trim();
  let query = lower;
  for (const [pattern, replacement] of REWRITES) query = query.replace(pattern, replacement);
  return query === lower ? term.trim() : query;
}

function el(tag, className) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function sized(url, width) {
  if (!url) return '';
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
}

function formatPrice(product) {
  const min = Number(product.price_min ?? product.price);
  const max = Number(product.price_max ?? product.price);
  if (!Number.isFinite(min)) return '';
  return (max > min ? 'From ' : '') + money.format(min);
}

/** Split `text` around the first case-insensitive match of `term`, wrapping the match in <mark>. */
function highlight(text, term) {
  const idx = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (idx < 0) return [document.createTextNode(text)];
  const mark = el('mark');
  mark.textContent = text.slice(idx, idx + term.length);
  return [document.createTextNode(text.slice(0, idx)), mark, document.createTextNode(text.slice(idx + term.length))];
}

class RchsPredictiveSearch extends HTMLElement {
  #input = null;
  #form = null;
  #panel = null;
  #status = null;
  #timer = 0;
  #controller = null;
  #cache = new Map();
  #open = false;
  #activeIndex = -1;

  #onDocumentPointerDown = (event) => {
    if (!this.contains(event.target)) this.close();
  };

  #onFocusOut = (event) => {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
  };

  connectedCallback() {
    this.#input = this.querySelector('input[type="search"]');
    this.#form = this.querySelector('form');
    this.#panel = this.querySelector('[data-rchs-panel]');
    this.#status = this.querySelector('[data-rchs-status]');
    if (!this.#input || !this.#panel) return;

    const input = this.#input;
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', this.#panel.id);

    input.addEventListener('input', () => this.#onInput());
    input.addEventListener('focus', () => {
      const term = this.#term();
      if (term.length >= MIN_CHARS && this.#cache.has(term)) this.#render(this.#cache.get(term), term);
    });
    input.addEventListener('keydown', (event) => this.#onKeyDown(event));
    if (this.#form) {
      this.#form.addEventListener('submit', () => {
        const query = normalizeQuery(input.value);
        if (query !== input.value.trim()) input.value = query;
      });
    }
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    this.#abort();
  }

  #term() {
    return this.#input.value.trim().replace(/\s+/g, ' ');
  }

  #onInput() {
    clearTimeout(this.#timer);
    const term = this.#term();
    if (term.length < MIN_CHARS) {
      this.#abort();
      this.close();
      return;
    }
    if (this.#cache.has(term)) {
      this.#render(this.#cache.get(term), term);
      return;
    }
    this.#timer = setTimeout(() => this.#fetch(term), DEBOUNCE_MS);
  }

  #abort() {
    if (this.#controller) {
      this.#controller.abort();
      this.#controller = null;
    }
    this.classList.remove('is-loading');
  }

  async #fetch(term) {
    this.#abort();
    const controller = new AbortController();
    this.#controller = controller;
    this.classList.add('is-loading');

    const base = (window.Theme && window.Theme.routes && window.Theme.routes.predictive_search_url) || '/search/suggest';
    const url = new URL(base + '.json', window.location.origin);
    url.searchParams.set('q', normalizeQuery(term));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(LIMIT));
    url.searchParams.set('resources[options][prefix]', 'last');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', FIELDS);

    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const data = await response.json();
      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      this.#remember(term, products);
      if (this.#term() === term) this.#render(products, term);
    } catch (error) {
      if (error && error.name !== 'AbortError') this.close();
    } finally {
      if (this.#controller === controller) {
        this.#controller = null;
        this.classList.remove('is-loading');
      }
    }
  }

  #remember(term, products) {
    if (this.#cache.size >= 60) this.#cache.delete(this.#cache.keys().next().value);
    this.#cache.set(term, products);
  }

  #searchUrl(term) {
    const action = (this.#form && this.#form.getAttribute('action')) || '/search';
    const url = new URL(action, window.location.origin);
    url.searchParams.set('q', normalizeQuery(term));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url.pathname + url.search;
  }

  #render(products, term) {
    this.#activeIndex = -1;
    const panel = this.#panel;
    const listId = panel.id + '-list';
    panel.replaceChildren();

    if (products.length) {
      const list = el('div', 'rchs-live__list');
      list.id = listId;
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', 'Matching products');
      products.forEach((product, index) => list.append(this.#row(product, index, term)));
      panel.append(list);
      this.#input.setAttribute('aria-controls', listId);
    } else {
      const empty = el('p', 'rchs-live__empty');
      empty.textContent = 'No products match “' + term + '”. Try a part number or model name.';
      panel.append(empty);
    }

    const all = el('a', 'rchs-live__all');
    all.href = this.#searchUrl(term);
    all.append(document.createTextNode(products.length ? 'See all results for ' : 'Search everything for '));
    const quoted = el('strong');
    quoted.textContent = '“' + term + '”';
    all.append(quoted);
    panel.append(all);

    if (this.#status) {
      this.#status.textContent = products.length
        ? products.length + (products.length === 1 ? ' product' : ' products') + ' found. Use the arrow keys to browse.'
        : 'No matching products.';
    }
    this.open();
  }

  #row(product, index, term) {
    const link = el('a', 'rchs-live__item');
    link.href = product.url;
    link.id = this.#panel.id + '-opt-' + index;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    link.tabIndex = -1;

    const imageWrap = el('span', 'rchs-live__img');
    const imageUrl = (product.featured_image && product.featured_image.url) || product.image || '';
    if (imageUrl) {
      const img = el('img');
      img.src = sized(imageUrl, 96);
      img.alt = '';
      img.width = 48;
      img.height = 48;
      img.loading = 'lazy';
      img.decoding = 'async';
      imageWrap.append(img);
    }

    const text = el('span', 'rchs-live__text');
    const title = el('span', 'rchs-live__title');
    const shown = product.title || '';
    const query = normalizeQuery(term);
    title.append(...highlight(shown, shown.toLowerCase().includes(query.toLowerCase()) ? query : term));
    const meta = el('span', 'rchs-live__meta');
    const sku = product.variants && product.variants[0] && product.variants[0].sku;
    if (sku) {
      const skuNode = el('span', 'rchs-live__sku');
      skuNode.textContent = '#' + sku;
      meta.append(skuNode);
    }
    const price = el('span', 'rchs-live__price');
    price.textContent = formatPrice(product);
    meta.append(price);
    text.append(title, meta);
    link.append(imageWrap, text);
    return link;
  }

  #items() {
    return Array.from(this.#panel.querySelectorAll('[role="option"]'));
  }

  #setActive(index) {
    const items = this.#items();
    if (!items.length) return;
    this.#activeIndex = ((index % items.length) + items.length) % items.length;
    items.forEach((item, i) => {
      const active = i === this.#activeIndex;
      item.setAttribute('aria-selected', String(active));
      item.classList.toggle('is-active', active);
    });
    const active = items[this.#activeIndex];
    active.scrollIntoView({ block: 'nearest' });
    this.#input.setAttribute('aria-activedescendant', active.id);
  }

  #onKeyDown(event) {
    if (event.key === 'Escape') {
      if (this.#open) {
        event.preventDefault();
        this.close();
      }
      return;
    }
    if (!this.#open) {
      const term = this.#term();
      if (event.key === 'ArrowDown' && term.length >= MIN_CHARS && this.#cache.has(term)) {
        event.preventDefault();
        this.#render(this.#cache.get(term), term);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#setActive(this.#activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#setActive(this.#activeIndex - 1);
    } else if (event.key === 'Enter' && this.#activeIndex >= 0) {
      const item = this.#items()[this.#activeIndex];
      if (item) {
        event.preventDefault();
        window.location.assign(item.href);
      }
    }
  }

  open() {
    if (this.#open) return;
    this.#open = true;
    this.#panel.hidden = false;
    this.classList.add('is-open');
    this.#input.setAttribute('aria-expanded', 'true');
  }

  close() {
    clearTimeout(this.#timer);
    if (!this.#open) return;
    this.#open = false;
    this.#panel.hidden = true;
    this.classList.remove('is-open');
    this.#input.setAttribute('aria-expanded', 'false');
    this.#input.removeAttribute('aria-activedescendant');
    this.#activeIndex = -1;
  }
}

if (!customElements.get('rchs-predictive-search')) {
  customElements.define('rchs-predictive-search', RchsPredictiveSearch);
}
